import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { SOLUTIONS_DATA } from "@/data/solutions";
import { SOLUTIONS_EN } from "@/data/solutions-en";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

export default function Solutions() {
    usePageMeta(
        { fr: "Nos solutions — WEYB", en: "Our solutions — WEYB" },
        { fr: "Développement international, programmes d'appui & capacity building, plateforme Export360, implantation & RH international.", en: "International development, support programmes & capacity building, Export360 platform, international setup & HR." }
    );
    const { lang, t, prefix } = useLang();
    const data = lang === "en" ? SOLUTIONS_EN : SOLUTIONS_DATA;

    return (
        <section data-testid="solutions-page" className="bg-white pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("solutionsPage.eyebrowIndex")}>{t("solutionsPage.eyebrow")}</Eyebrow>
                <Reveal>
                    <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight text-weyb-ink leading-tight max-w-4xl">
                        {t("solutionsPage.title")}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
                        {t("solutionsPage.intro")}
                    </p>
                </Reveal>

                <div className="mt-16 border-t border-gray-200">
                    {data.map((s, i) => (
                        <Reveal key={s.slug} delay={i * 0.06}>
                            <Link
                                to={`${prefix}/solutions/${s.slug}`}
                                data-testid={`solutions-row-${s.slug}`}
                                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-center py-10 md:py-12 border-b border-gray-200 transition-colors hover:bg-[#F9FAFB] -mx-6 md:-mx-12 px-6 md:px-12"
                            >
                                <span className="md:col-span-1 font-display text-3xl font-light text-weyb-yellow">{s.num}</span>
                                <div className="md:col-span-6">
                                    <h2 className="font-display text-2xl md:text-3xl font-medium text-weyb-ink tracking-tight group-hover:translate-x-1 transition-transform">
                                        {s.title}
                                    </h2>
                                    <p className="mt-2 text-sm md:text-base text-gray-600">{s.tagline}</p>
                                </div>
                                <p className="md:col-span-4 text-sm text-gray-500 leading-relaxed line-clamp-2">
                                    {s.heroDesc}
                                </p>
                                <span className="md:col-span-1 flex md:justify-end">
                                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-gray-300 text-weyb-ink group-hover:bg-weyb-yellow group-hover:border-weyb-yellow transition-colors">
                                        <ArrowRight size={17} />
                                    </span>
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15} className="mt-16">
                    <div className="bg-weyb-ink text-white rounded-sm p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
                        <div>
                            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight">
                                {t("solutionsPage.ctaTitle")}
                            </h2>
                            <p className="mt-3 text-sm text-white/60 max-w-md">
                                {t("solutionsPage.ctaText")}
                            </p>
                        </div>
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="solutions-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors shrink-0"
                        >
                            {t("solutionsPage.ctaButton")}
                            <ArrowUpRight size={16} />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
